const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const formatQtyLabel = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "0";
  const rounded = Number(parsed.toFixed(3));
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(3).replace(/\.?0+$/, "");
};

export const nowDateString = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = `${now.getMonth() + 1}`.padStart(2, "0");
  const dd = `${now.getDate()}`.padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const buildPresentationOptions = (productDetail) => {
  const today = nowDateString();
  const presentations = toArray(productDetail?.presentations).filter((row) => row?.is_active);
  return presentations
    .map((presentation) => {
      if (presentation?.base_sale_price != null) {
        return {
          id: presentation.id,
          product_id: presentation?.product?.id || productDetail?.id || null,
          name: presentation?.product_format?.name || "Presentacion",
          format_name: presentation?.product_format?.name || "Presentacion",
          qty_in_base: Number(presentation?.qty_in_base || 0),
          cost_price: Number(presentation?.cost_price || 0),
          base_sale_price: Number(presentation?.base_sale_price || 0),
          stock_qty_base: Number(presentation?.stock_qty_base || 0),
        };
      }

      const prices = toArray(presentation?.prices).filter((price) => price?.is_active);
      const activePrice =
        prices.find((price) => price?.date_begin <= today && price?.date_end >= today) ||
        prices[0] ||
        null;
      if (!activePrice) return null;
      return {
        id: presentation.id,
        product_id: presentation?.product?.id || productDetail?.id || null,
        name: presentation?.product_format?.name || "Presentacion",
        format_name: presentation?.product_format?.name || "Presentacion",
        qty_in_base: Number(presentation?.qty_in_base || 0),
        cost_price: Number(presentation?.cost_price || 0),
        base_sale_price: Number(activePrice?.sale_price || 0),
        stock_qty_base: Number(presentation?.stock_qty_base || 0),
      };
    })
    .filter(Boolean);
};

export const formatStockHumanized = (qtyAvailableBase, presentationOptions = []) => {
  const safeQtyAvailableBase = Math.max(0, Math.floor(Number(qtyAvailableBase || 0)));
  const sortedOptions = toArray(presentationOptions)
    .map((option) => ({
      ...option,
      qty_in_base: Number(option?.qty_in_base || 0),
      label: String(option?.format_name || option?.name || "unidad").trim(),
    }))
    .filter((option) => Number.isFinite(option.qty_in_base) && option.qty_in_base > 0)
    .sort((left, right) => right.qty_in_base - left.qty_in_base);

  if (safeQtyAvailableBase <= 0 || sortedOptions.length === 0) {
    return "Sin stock";
  }

  let remainder = safeQtyAvailableBase;
  const parts = [];

  sortedOptions.forEach((option) => {
    const units = Math.floor(remainder / option.qty_in_base);
    remainder %= option.qty_in_base;
    if (units > 0) {
      parts.push(`${units} ${option.label}`);
    }
  });

  if (parts.length === 0) {
    const fallback = sortedOptions[sortedOptions.length - 1];
    return `0 ${fallback.label}`;
  }

  return parts.join(" · ");
};

export const getMaxUnitsFromStockBase = (stockBase, qtyInBase) => {
  const safeStockBase = Number(stockBase);
  const safeQtyInBase = Number(qtyInBase);
  if (!Number.isFinite(safeStockBase) || !Number.isFinite(safeQtyInBase) || safeQtyInBase <= 0) {
    return 0;
  }
  return Math.max(0, Math.floor(safeStockBase / safeQtyInBase));
};

export const buildStockDetailPatch = (rows = []) => {
  return toArray(rows).reduce((accumulator, row) => {
    const key = String(row?.product_presentation_id || "");
    if (!key) return accumulator;
    accumulator[key] = {
      qty_base: Number(row?.qty_base ?? 0),
      qty_available: Number(row?.qty_available ?? row?.qty_base ?? 0),
      qty_reserved_soft: Number(row?.qty_reserved_soft ?? 0),
      qty_reserved_hard: Number(row?.qty_reserved_hard ?? 0),
    };
    return accumulator;
  }, {});
};

export const getStockReservationStats = (detail = null, fallbackAvailableBase = 0) => {
  const qtyAvailable = Math.max(0, Number(detail?.qty_available ?? fallbackAvailableBase ?? 0));
  const qtyReservedSoft = Math.max(0, Number(detail?.qty_reserved_soft ?? 0));
  const qtyReservedHard = Math.max(0, Number(detail?.qty_reserved_hard ?? 0));
  const qtyReserved = qtyReservedSoft + qtyReservedHard;
  const qtyBaseRaw = Number(detail?.qty_base ?? qtyAvailable + qtyReserved);
  const qtyBase = Math.max(qtyAvailable + qtyReserved, Number.isFinite(qtyBaseRaw) ? qtyBaseRaw : 0);

  return {
    qtyBase,
    qtyAvailable,
    qtyReserved,
    qtyReservedSoft,
    qtyReservedHard,
  };
};

export const buildStockStatusMeta = (stats, presentationOptions = []) => {
  const qtyBase = Math.max(0, Number(stats?.qtyBase || 0));
  const qtyAvailable = Math.max(0, Number(stats?.qtyAvailable || 0));
  const qtyReserved = Math.max(0, Number(stats?.qtyReserved || 0));
  const totalLabel = formatStockHumanized(qtyBase, presentationOptions);
  const reservedLabel = formatStockHumanized(qtyReserved, presentationOptions);
  const availableLabel = formatStockHumanized(qtyAvailable, presentationOptions);
  const cleanAvailableLabel = availableLabel === "Sin stock" ? "0" : availableLabel;
  const cleanReservedLabel = reservedLabel === "Sin stock" ? "0" : reservedLabel;
  const cleanTotalLabel = totalLabel === "Sin stock" ? "0" : totalLabel;

  if (qtyAvailable > 0) {
    return {
      status: "available",
      label: `Disponible: ${cleanAvailableLabel}`,
      badgeLabel: "Disponible",
      color: "success",
      hasStock: true,
      canFulfillRequested: true,
      tooltip: `Stock total: ${cleanTotalLabel}. Reservado: ${cleanReservedLabel}. Disponible: ${cleanAvailableLabel}.`,
    };
  }

  if (qtyReserved > 0 || qtyBase > 0) {
    return {
      status: "reserved",
      label: `En reserva: ${cleanReservedLabel} / Disponible: 0`,
      badgeLabel: "En reserva",
      color: "warning",
      hasStock: true,
      canFulfillRequested: false,
      tooltip: `Stock total: ${cleanTotalLabel}. Reservado: ${cleanReservedLabel}. Disponible: 0.`,
    };
  }

  return {
    status: "out",
    label: "Sin stock",
    badgeLabel: "Sin stock",
    color: "default",
    hasStock: false,
    canFulfillRequested: false,
    tooltip: "Stock total: 0. Reservado: 0. Disponible: 0.",
  };
};

export const buildLiveStockLineMeta = ({
  detail = null,
  availableBase = 0,
  ownCapturedBase = 0,
  presentationOptions = [],
} = {}) => {
  const stats = getStockReservationStats(detail, availableBase);
  const ownCaptured = Math.max(0, Number(ownCapturedBase || 0));
  const capturedByOthers = Math.max(0, Number(stats.qtyReserved || 0));
  const stockTotal = Math.max(stats.qtyBase, stats.qtyAvailable + capturedByOthers);
  const availableForDraft = Math.max(0, Number(stats.qtyAvailable || 0));
  const availableRemaining = Math.max(0, availableForDraft - ownCaptured);
  const totalLabel = formatStockHumanized(stockTotal, presentationOptions);
  const othersLabel = formatStockHumanized(capturedByOthers, presentationOptions);
  const ownLabel = formatStockHumanized(ownCaptured, presentationOptions);
  const availableLabel = formatStockHumanized(availableRemaining, presentationOptions);

  return {
    stockTotal,
    capturedByOthers,
    capturedOwn: ownCaptured,
    availableForDraft,
    availableRemaining,
    totalLabel: totalLabel === "Sin stock" ? "0" : totalLabel,
    othersLabel: othersLabel === "Sin stock" ? "0" : othersLabel,
    ownLabel: ownLabel === "Sin stock" ? "0" : ownLabel,
    availableLabel: availableLabel === "Sin stock" ? "0" : availableLabel,
  };
};

export const getStockAvailabilityMeta = (stockBase, qtyInBase, requestedQty = 1) => {
  const safeStockBase = Number(stockBase);
  const safeQtyInBase = Number(qtyInBase);
  const safeRequestedQty = Math.max(1, Number(requestedQty) || 1);

  if (!Number.isFinite(safeStockBase)) {
    return {
      availableUnits: null,
      maxUnits: 0,
      label: "-",
      color: "default",
      hasStock: false,
      canFulfillRequested: false,
    };
  }

  if (!Number.isFinite(safeQtyInBase) || safeQtyInBase <= 0) {
    return {
      availableUnits: 0,
      maxUnits: 0,
      label: "0",
      color: "error",
      hasStock: false,
      canFulfillRequested: false,
    };
  }

  const availableUnits = safeStockBase / safeQtyInBase;
  const maxUnits = Math.max(0, Math.floor(availableUnits));

  if (availableUnits <= 0) {
    return {
      availableUnits: 0,
      maxUnits: 0,
      label: "0",
      color: "error",
      hasStock: false,
      canFulfillRequested: false,
    };
  }

  const label = Number.isInteger(availableUnits)
    ? String(availableUnits)
    : availableUnits.toFixed(2);

  if (availableUnits < safeRequestedQty) {
    return {
      availableUnits,
      maxUnits,
      label,
      color: "warning",
      hasStock: true,
      canFulfillRequested: false,
    };
  }

  return {
    availableUnits,
    maxUnits,
    label,
    color: "success",
    hasStock: true,
    canFulfillRequested: true,
  };
};

export const selectPreferredPresentationOption = (options, stockBaseByPresentation = {}) => {
  if (!Array.isArray(options) || options.length === 0) return null;

  const ranked = options
    .map((option) => ({
      option,
      maxUnits: getMaxUnitsFromStockBase(stockBaseByPresentation[String(option.id)] ?? 0, option.qty_in_base),
    }))
    .sort((left, right) => {
      if (right.maxUnits !== left.maxUnits) {
        return right.maxUnits - left.maxUnits;
      }
      const leftQty = Number(left.option?.qty_in_base || 0);
      const rightQty = Number(right.option?.qty_in_base || 0);
      if (leftQty !== rightQty) {
        return leftQty - rightQty;
      }
      return String(left.option?.name || "").localeCompare(String(right.option?.name || ""));
    });

  const withStock = ranked.find((entry) => entry.maxUnits > 0);
  return withStock || ranked[0];
};

export const buildProductStockMeta = (
  options,
  stockBaseByPresentation = {},
  stockDetailByPresentation = {}
) => {
  const safeOptions = toArray(options);
  let selected = selectPreferredPresentationOption(safeOptions, stockBaseByPresentation);
  if (selected?.maxUnits <= 0) {
    const reservedOption = safeOptions.find((option) => {
      const key = String(option?.id || "");
      const stats = getStockReservationStats(
        stockDetailByPresentation[key],
        stockBaseByPresentation[key] ?? option?.stock_qty_base ?? 0
      );
      return stats.qtyReserved > 0 || stats.qtyBase > 0;
    });
    if (reservedOption) {
      selected = {
        option: reservedOption,
        maxUnits: 0,
      };
    }
  }
  if (!selected) {
    return {
      presentationId: null,
      presentationName: "",
      unit_price: 0,
      maxUnits: 0,
      label: "Sin stock",
      color: "default",
      hasStock: false,
      canFulfillRequested: false,
      badgeLabel: "Sin stock",
      status: "out",
      tooltip: "Stock total: 0. Reservado: 0. Disponible: 0.",
    };
  }

  const selectedKey = String(selected.option.id);
  const stats = getStockReservationStats(
    stockDetailByPresentation[selectedKey],
    stockBaseByPresentation[selectedKey] ?? selected.option?.stock_qty_base ?? 0
  );
  const statusMeta = buildStockStatusMeta(stats, safeOptions);
  const stockMeta = getStockAvailabilityMeta(
    stats.qtyAvailable,
    selected.option.qty_in_base,
    1
  );

  return {
    presentationId: selected.option.id,
    presentationName: selected.option.name,
    unit_price: Number(selected.option?.base_sale_price || 0),
    maxUnits: stockMeta.maxUnits,
    label: statusMeta.label,
    badgeLabel: statusMeta.badgeLabel,
    status: statusMeta.status,
    tooltip: statusMeta.tooltip,
    qty_available_base: stats.qtyAvailable,
    qty_base: stats.qtyBase,
    qty_reserved_base: stats.qtyReserved,
    qtyBaseLabel: formatQtyLabel(stats.qtyBase),
    capturedLabel: formatQtyLabel(stats.qtyReserved),
    availableLabel: formatQtyLabel(stats.qtyAvailable),
    color: statusMeta.color || stockMeta.color,
    hasStock: statusMeta.hasStock,
    canFulfillRequested: stockMeta.canFulfillRequested,
  };
};
